import React from 'react';
import { navigate } from 'gatsby';
import path from 'path-browserify';

import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import HeightIcon from '@material-ui/icons/Height';

import { bubbleChartColors } from './helpers/chartHelpers';

export const staticImageFolder = '/images';
export const logoPath = `${staticImageFolder}/logo_meteofrance.png`;

export const getLink = (language, link) => path.join(`/${language}`, link);

export const homeContentName = 'accueil';
export const linksContentName = 'liens';
export const mentionsLegalesContentName = 'mentions_legales';
export const currentDomainLayout = 'currentDomain';
export const domainMapPickerLayout = 'myArea';

/**
 * Synthesis page has 2 tabs : Domain map + Domain selector with map
 * This function return the tab(s) depending on domain code name and type
 *
 * @param {string} domainCode
 * @param {string} domainName
 * @param {string} domainType
 * @returns {array of object}
 */
export const getRegionSelectorTabs = (domainCode, domainName, domainType) => {
  const defaultTabs = [
    { label: domainName, id: currentDomainLayout, icon: null },
    { label: 'autre region', id: domainMapPickerLayout, icon: null },
  ];

  // for metropolitan france, we want to only display
  // regions choices
  if (domainCode === 'FRA') {
    return [
      { label: 'ma region', id: domainMapPickerLayout, icon: null },
    ];
  }

  // outside of regional types, we want to only display
  // the current domain image
  if (domainType !== 'REG') {
    return defaultTabs.slice(0, 1);
  }

  return defaultTabs;
};

/**
 * Trigger navigation to the given link (taking language into account)
 * using Gatbsy navigate function
 *
 * @param {string} language
 * @param {string} link
 */
export const triggerNavigation = (language, link) => {
  navigate(getLink(language, link));
};

const icons = {
  staticScale: <AspectRatioIcon />,
  dynamicScale: <HeightIcon />,
};

export const imageContentConfig = {
  img: ['IMAGE', 'IMAG2'],
  imgLabels: [
    { id: 0, label: 'echelle_statique', icon: icons.staticScale },
    { id: 1, label: 'echelle_dynamique', icon: icons.dynamicScale },
  ],
};

export const footerLinks = [
  { label: 'lien_contact', link: '/contact' },
  { label: 'lien_actu', link: 'https://meteofrance.com/actualites/climat' },
  { label: 'lien_offre', link: 'https://meteofrance.com/changement-climatique/nos-services-climatiques' },
  { label: 'lien_util', link: '/liens' },
  { label: 'mentions_legales', link: 'https://meteofrance.com/mentions-legales' },
];
/**
* Old links
*
*{ label: 'lien_contact', link: 'http://www.meteo.fr/meteonet/temps/clim/ClimatHD/menu.html?domaine=FRA&periode=PASSE&langue=fr#' },
*{ label: 'lien_actu', link: 'http://www.meteofrance.fr/climat-passe-et-futur' },
*{ label: 'lien_offre', link: 'http://www.meteofrance.fr/documents/10192/35608/Catalogue_Services_Climatiques.pdf' },
*/

export const maxRightVignette = 4;
export const yearsTicks = { start: 1900, end: 2100, step: 20, stepTick: 4, switchColorYear: 2020 };

/*
 * Grid Image Layout (eg : FUTUR / FRA/ PRECI
 *
 * { column 1
 *   content : [
 *      { row 1 },
 *      { row 2 },...
 *   ]
 * },
 * { column 2
 *   content : [
 *      { row 1 },
 *      { row 2 },...
 *   ]
 * }
*/
export const defaultGridLayout = [
  {
    xs: 4,
    isContainer: true,
    id: 'left',
    content: [
      { id: 'REF', xs: 12, isContainer: false, textContent: 'event_ref' },
    ],
  },
  {
    xs: 8,
    isContainer: true,
    id: 'right',
    content: [
      { id: 'HZM45', xs: 6, isContainer: false, textContent: 'event_hm_45' },
      { id: 'HZL45', xs: 6, isContainer: false, textContent: 'event_hl_45' },
      { id: 'HZM85', xs: 6, isContainer: false, textContent: 'event_hm_85' },
      { id: 'HZL85', xs: 6, isContainer: false, textContent: 'event_hl_85' },
    ],
  },
];

export const gridLayoutHZ = [
  {
    xs: 12,
    isContainer: true,
    id: 'left',
    content: [
      { id: 'REF', xs: 4, isContainer: false, textContent: 'ref_ANT_precip' },
      { id: 'HZ1', xs: 4, isContainer: false, textContent: 'event_h_1' },
      { id: 'HZ2', xs: 4, isContainer: false, textContent: 'event_h_2' },
    ],
  },
];

export const gridLayoutRCP = [
  {
    xs: 12,
    isContainer: true,
    id: 'top',
    content: [
      { id: 'RCP26', xs: 4, isContainer: false, textContent: 'preci_rcp26_emission' },
      { id: 'RCP45', xs: 4, isContainer: false, textContent: 'preci_rcp45_emission' },
      { id: 'RCP85', xs: 4, isContainer: false, textContent: 'preci_rcp85_emission' },
    ],
  },
  {
    xs: 12,
    isContainer: true,
    id: 'down',
    content: [
      { id: '', xs: 4, isContainer: false, textContent: '' },
    ],
  },
];

export const gridLayoutRCP_bis = [
  {
    xs: 12,
    isContainer: true,
    id: 'down',
    content: [
      { id: 'REF', xs: 4, isContainer: false, textContent: 'event_ref' },
    ],
  },
  {
    xs: 12,
    isContainer: true,
    id: 'top',
    content: [
      { id: 'RCP26', xs: 4, isContainer: false, textContent: 'event_emission1' },
      { id: 'RCP45', xs: 4, isContainer: false, textContent: 'event_emission2' },
      { id: 'RCP85', xs: 4, isContainer: false, textContent: 'event_emission3' },
    ],
  },
];

export const alertMessageDeplay = 6000; // milliseconds

export const defaultFamilyDisplayName = 'parametre';
export const familiesDisplayName = {
  TEMPE: defaultFamilyDisplayName,
  PRECI: defaultFamilyDisplayName,
  EVENT: 'event_family_list',
  IMPAC: 'impac_family_list',
};

export const homePageConfiguration = {
  gradient: 'radial-gradient(at 20% 25%, #00daf0 0% ,#0283a7 60%, #004468 100%)',
  // gradient: 'linear-gradient(27deg, rgba(0,114,152,1) 0%, rgba(0,145,179,1) 100%)',
  // gradient: 'linear-gradient(170deg, rgba(34,216,244,1) 0%, rgba(8,131,168,1) 50%, rgba(8,89,125,1) 100%)',
  imageMaxHeight: '300px',
};

export const bubbleChartCompareButtons = [
  {
    code: 'compare_scenario',
    child: [
      {
        code: 'horizon_m',
        series: [
          { code: 'rcp26Horizon1', label: 'event_2.6', color: { chaleur: bubbleChartColors.darkOrange, froid: bubbleChartColors.darkBlue } },
          { code: 'rcp85Horizon1', label: 'event_8.5', color: { chaleur: bubbleChartColors.darkPink, froid: bubbleChartColors.violet } },
        ],
      },
      {
        code: 'horizon_l',
        series: [
          { code: 'rcp26Horizon2', label: 'event_2.6', color: { chaleur: bubbleChartColors.darkOrange, froid: bubbleChartColors.darkBlue } },
          { code: 'rcp85Horizon2', label: 'event_8.5', color: { chaleur: bubbleChartColors.darkPink, froid: bubbleChartColors.violet } },
        ],
      },
    ],
  },
  {
    code: 'compare_horizon',
    child: [
      {
        code: 'event_2.6',
        series: [
          { code: 'rcp26Horizon1', label: 'horizon_m', color: { chaleur: bubbleChartColors.darkOrange, froid: bubbleChartColors.darkBlue } },
          { code: 'rcp26Horizon2', label: 'horizon_l', color: { chaleur: bubbleChartColors.darkPink, froid: bubbleChartColors.violet } },
        ],
      },
      {
        code: 'event_8.5',
        series: [
          { code: 'rcp85Horizon1', label: 'horizon_m', color: { chaleur: bubbleChartColors.darkOrange, froid: bubbleChartColors.darkBlue } },
          { code: 'rcp85Horizon2', label: 'horizon_l', color: { chaleur: bubbleChartColors.darkPink, froid: bubbleChartColors.violet } },
        ],
      },
    ],
  },
];

export const getSerieConfig = (itemCode = '', compareButtonsConfig = [], scenario) => {
  const currentSerie = compareButtonsConfig.find(({ code }) => code === scenario?.compare)?.child
    .find(({ code }) => code === scenario?.variant)?.series;

  return currentSerie?.find(({ code }) => code === itemCode);
};
